// Boolean(), not the bare `&&`/`||` chain: env vars are `string | undefined`, so the
// chain's type is `string | boolean`, which every consumer then has to coerce at the
// point of use (and some silently pass a string where a boolean is expected).
export const IS_CAP = Boolean(typeof process != 'undefined' && (process.env.NEXT_PUBLIC_BUILD_FOR_CAPACITOR || process.env.BUILD_FOR_CAPACITOR))
export const IS_CAP_STG = Boolean(typeof process != 'undefined' && (process.env.NEXT_PUBLIC_BUILD_FOR_CAPACITOR_STG || process.env.BUILD_FOR_CAPACITOR_STG))
export const IS_PROD = typeof process != 'undefined' && process.env.NODE_ENV === "production" || IS_CAP;
export const IS_DEV = typeof process != 'undefined' && process.env.NODE_ENV === "development";
export const IS_IOS = typeof process != 'undefined' && process.env.BUILD_TARGET === "ios";
export const IS_ANDROID = typeof process != 'undefined' && process.env.BUILD_TARGET === "android";
export const IS_MOBILE = IS_IOS || IS_ANDROID || IS_CAP || IS_CAP_STG;
/**
 * Call-time forms. The constants below snapshot these at module load, which is correct
 * for module-scope config (a bucket name computed once) but wrong anywhere the value is
 * read per request or per test — tests set VERCEL_ENV between cases, and a snapshot
 * taken at import time cannot see that. Use the function where the value is read inside
 * a function body, the constant where it initialises module scope.
 *
 * These are the ONLY places the raw environment variables are named — see
 * testing/jest/utils/staging-detection.test.ts, which fails if a copy appears elsewhere.
 */
export function isPreviewEnv(): boolean {
  return typeof process != 'undefined' && (process.env.VERCEL_ENV === "preview" || process.env.NEXT_PUBLIC_VERCEL_ENV === "preview")
}

/** True for a Capacitor staging build, which runs from a laptop against staging. */
export function isCapStagingBuild(): boolean {
  return Boolean(typeof process != 'undefined' && (process.env.NEXT_PUBLIC_BUILD_FOR_CAPACITOR_STG || process.env.BUILD_FOR_CAPACITOR_STG))
}

/** "Behave as staging" — a preview deployment OR a Capacitor staging build. */
export function isStagingEnv(): boolean {
  return isPreviewEnv() || isCapStagingBuild()
}

/**
 * The deployment's environment as a label, for ledgers that record where a row was
 * written: 'development' | 'staging' | 'production'.
 *
 * NOT `NODE_ENV` — that is 'production' on a preview deployment too, so using it makes
 * staging and production rows indistinguishable after the fact.
 */
export function getDeploymentEnvironment(): 'development' | 'staging' | 'production' {
  if (isDevEnv()) return 'development'
  // isPreviewEnv rather than isStagingEnv: this is a deployment label, and a Capacitor
  // staging build is already covered by the development branch above.
  if (isPreviewEnv()) return 'staging'
  return 'production'
}

/**
 * A staging web build, by the API host baked into it. Every web build sets
 * NEXT_PUBLIC_API_URL (new.sellwild.com on staging, sellwild.com on prod and the
 * prod app), while the Vercel environment vars are absent from some staging
 * client bundles -- which is why the browse tag pills, gated on isStagingEnv()
 * alone, never showed on new.sellwild.com.
 */
export function isStagingHostBuild(): boolean {
  const api = typeof process != 'undefined' ? process.env.NEXT_PUBLIC_API_URL ?? '' : '';
  return api.includes('new.sellwild.com') || api.includes('dev.sellwild.com');
}

/** True in local development. */
export function isDevEnv(): boolean {
  return typeof process != 'undefined' && process.env.NODE_ENV === "development"
}

export const IS_PREVIEW = isPreviewEnv()
export const IS_STG = IS_PREVIEW || IS_CAP_STG;
export const NOTIFICATION_AUTOCLOSE_TIMEOUT = 2000;

// Force local database in development
export const useStagingDB = IS_DEV || IS_STG; // Always use staging DB in dev and staging environments
// export const useStagingDB = IS_STG; // Staging only
// export const useStagingDB = false // Test production data.

// Add missing export
export const FACEBOOK_TYPE = "facebook";

// DEPRECATED: Hard-coded sellers are no longer used. Username-based routing is now handled dynamically.
// Kept for backwards compatibility but should not be used in new code.
// const DEV_SELLERS = [
//   {
//     slug: "petfinder",
//     id: 6781852,
//   },
//   {
//     slug: "bargainhunter",
//     id: 82,
//   },
// ];
//
// const PROD_SELLERS = [
//   {
//     slug: "sella",
//     id: 95088975,
//   },
//   {
//     slug: "gcc",
//     id: 74569925,
//   },
//   {
//     slug: "myev",
//     id: 94977342,
//   },
//   {
//     slug: "petfinder",
//     id: 95089031,
//   },
// ];
//
// const DEV_TRUSTED_SELLERS = [
//   82, 6781861, 6781860, 6781858, 6781851, 6781846, 6781843, 6781842, 6781841,
//   6781834, 6781824, 6781790, 6781770,
// ];
//
// const PROD_TRUSTED_SELLERS = [
//   95089264, 95089263, 95089223, 95089205, 95089189, 95089141, 95089138,
//   95089136, 95089135, 95089123, 95089060, 95089058, 95089045, 95089044,
//   95089028, 95089003, 95088988, 95088976, 95088975, 95088971, 95088947,
//   95088945, 95088942, 95088855, 95088841, 95088834, 95088809, 95088792,
//   95088780, 95088757, 95088721, 95088719, 95088712, 95088698, 95088690,
//   95088687, 95088674, 95088667, 95088660, 95088645, 95088638, 95088637,
//   95054120, 95043025, 95042923, 95029842, 95028220, 95024638, 91219527,
//   76060618, 74690230, 74684652, 74572703, 74565178,
// ];
//
// export let HARD_CODED_SELLERS = IS_PROD ? PROD_SELLERS : DEV_SELLERS;
//
// export const HARD_CODED_SELLER_IDS = IS_MOBILE ? [] : (IS_PROD
//   ? PROD_TRUSTED_SELLERS
//   : DEV_TRUSTED_SELLERS);
// export const HARD_CODED_SELLER_SLUGS = IS_MOBILE ? [] : HARD_CODED_SELLERS.map((v) => v.slug);

// Generic section
export const UNDEFINED = 0;
export const SERVER_UNAVAILABLE = 1;
export const METHOD_IS_DISABLED = 2;
export const STUB_METHOD = 3;
export const HTTP_FAILED = 4;
// End of Generic section

// Authentication section
/** Returned in case of invalid cridential data */
export const ACCESS_DENIED = 10000;
export const FAILED_TO_START_SESSION = 11001;
export const INVALID_TOKEN = 11002;
export const BANNED_USER = 11003;
export const BANNED_EMAIL = 11004;
export const TWO_FACTOR_REQUIRED = 11005;
export const TWO_FACTOR_INVALID = 11006;
export const FAILED_RECAPTCHA_REQUEST = 11007;
export const RECAPTCHA_RISK_TOO_HIGH = 11008;

// End of Authentication section

// Database section
export const DB_FAILED_TO_CONNECT = 20000;
export const DB_INVALID_QUERY = 20001;
export const DB_INCONSISTENT_DATA = 20002;
// End of Database section

// Application section
export const INVALID_APP_CFG = 30000;

// Core modules subsection
// User module
// core errors
export const USER_REGISTER_FAILED = 30100;
export const USER_REGISTER_INVALID_NAME = 30101;
export const USER_REGISTER_INVALID_NAME_LENGTH = 30102;
export const USER_REGISTER_DUPLICATE_NAME = 30103;

export const USER_REGISTER_INVALID_PASSWORD = 30104;
export const USER_REGISTER_INVALID_PASSWORD_LENGTH = 30105;
export const USER_REGISTER_PASSWORD_NOT_MATCH = 30106;

export const USER_REGISTER_INVALID_EMAIL = 30107;
export const USER_REGISTER_DUPLICATE_EMAIL = 30108;

export const USER_REGISTER_INVALID_PHOTO = 30109;
export const USER_REGISTER_INVALID_PHOTO_TOO_LARGE = 30110;
export const USER_REGISTER_FAILED_TO_PROCESS_PHOTO = 30111;

export const USER_CONFIRM_REGISTER_INVALID_CONFIRMATION_CODE = 30112;

export const USER_REGISTER_INVALID_CAPTCHA = 30113;

export const USER_REGISTER_INVALID_FIRST_NAME = 30114;
export const USER_REGISTER_INVALID_FIRST_NAME_LENGTH = 30115;

export const USER_REGISTER_INVALID_LAST_NAME = 30116;
export const USER_REGISTER_INVALID_LAST_NAME_LENGTH = 30117;

// custom errors
export const FAILED_REGISTER_INVALID_DEVICE_ID = 30118;
export const FAILED_REGISTER_DUPLICATE_DEVICE_ID = 30119;

export const USER_REGISTER_INVALID_PHONE = 30120;
export const USER_REGISTER_INVALID_PHONE_LENGTH = 30121;

export const USER_REGISTER_INVALID_REFERRAL_ID = 30122;

export const USER_REGISTER_INVALID_TWITTER_NAME = 30123;
export const USER_REGISTER_TWITTER_NAME_IS_PARKED = 30124;
export const USER_REGISTER_INVALID_TWITTER_NAME_LENGTH = 30125;
export const USER_REGISTER_DUPLICATE_TWITTER_NAME = 30126;
export const USER_REGISTER_INVALID_TWITTER_CREDENTIALS = 30127;

export const USER_REGISTER_INVALID_MEMBERSHIP_TYPE = 30128;

export const USER_REGISTER_INVALID_USER_TYPE = 30136;
export const USER_REGISTER_INVALID_REMOTE_API_CREDENTIALS = 30137;
export const USER_REGISTER_DUPLICATE_REMOTE_API_USER_ID = 30138;
export const USER_REGISTER_DUPLICATE_REMOTE_API_CREDENTIALS = 30139;

export const USER_REGISTER_NAME_IS_PARKED = 30131;
export const USER_INVALID_USER_ID = 30132;

export const FAILED_REGISTER_INVALID_DEVICE_TOKEN = 30133;

export const USER_REGISTER_INVALID_FACEBOOK_CREDENTIALS = 30134;
export const INVALID_CONFIRMATION = 30135;

export const USER_MISMATCHED_CARRIER = 30136;
export const USER_MISMATCHED_PHONE = 30137;

export const USER_REGISTER_INVALID_PAYPAL_CREDENTIALS = 30139;
export const USER_REGISTER_INVALID_RESET_PASS_USER_TYPE = 30140;

export const USER_STRIPECONNECT_INVALID_ACCOUNT = 30141;
export const USER_STRIPECONNECT_ONBOARDING_ISSUE = 30142;

export const USER_SEARCH_EMPTY = 30150;

export const PARTNER_CREATE_FAILED = 30160;

// End of User module
// End of Core modules subsection

export const LISTING_CREATE_FAILED = 30200;
export const LISTING_CREATE_INVALID_TEXT = 30201;
export const LISTING_CREATE_INVALID_TEXT_LENGTH = 30202;
export const LISTING_CREATE_INVALID_CATEGORY_ID = 30203;
export const LISTING_CREATE_INVALID_PRICE = 30204;
export const LISTING_CREATE_INVALID_PRICE_LENGTH = 30206;
export const LISTING_CREATE_INVALID_PHOTO = 30207;
export const LISTING_CREATE_INVALID_PHOTO_TOO_LARGE = 30208;
export const LISTING_CREATE_FAILED_TO_PROCESS_PHOTO = 30209;
export const LISTING_CREATE_FAILED_GEO = 30210;
export const LISTING_CREATE_INVALID_LONGITUDE = 30211;
export const LISTING_CREATE_INVALID_LATITUDE = 30212;

export const LISTING_SEARCH_INVALID_Q = 30213;
export const LISTING_SEARCH_INVALID_Q_LENGTH = 30214;
export const LISTING_SEARCH_INVALID_CATEGORY = 30215;
export const LISTING_SEARCH_INVALID_CAT_GROUP = 30216;
export const LISTING_SEARCH_INVALID_DISTANCE = 30217;
export const LISTING_SEARCH_FAILED_GEO = 30218;
export const LISTING_SEARCH_FAILED = 30220;

export const LISTING_DELETE_INVALID_ID = 30221;
export const LISTING_DELETE_FAILDED = 30222;

export const LISTING_ATTACH_TW_INVALID_ID = 30223;
export const LISTING_ATTACH_TW_INVALID_TWITTER_ID = 30224;

export const LISTING_GET_BY_ID_INVALID_ID = 30225;

export const LISTING_SEARCH_EMPTY = 30226;
export const LISTING_CREATE_INVALID_DATE = 30227;

export const LISTING_ALREADY_WATCHING = 30228;

export const LISTING_REPORTING_FAILED = 30229;

export const LISTING_FAILED_PURITY_CHECK = 30230;

export const LISTING_SEARCH_EXCLUDED_CATEGORIES = 30231;

export const LISTING_CREATE_INVALID_WEIGHT = 30232;
export const LISTING_CREATE_INVALID_DIMENSIONS = 30233;

export const GEO_GEOCODE_FAILED = 30300;
export const GEO_GEOCODE_NO_CONTENT = 30301;
export const GEO_GEOCODE_INVALID_REGION = 30302;

export const CHAT_INVALID_CHAT_ID = 30400;
export const CHAT_SEND_MESSAGE_INVALID_LISTING_ID = 30401;
export const CHAT_SEND_MESSAGE_INVALID_OWNER_ID = 30402;
export const CHAT_SEND_MESSAGE_INVALID_LISTING_TYPE = 30403;
export const CHAT_SEND_MESSAGE_INVALID_TEXT = 30404;
export const CHAT_SEND_MESSAGE_CREATE_NEW_CHAT_FAILED = 30405;
export const CHAT_SEND_MESSAGE_UPDATE_CHAT_FAILED = 30406;
export const CHAT_ACCESS_DENIED = 30407;
export const CHAT_INVALID_MESSAGE_ID = 30408;
export const CHAT_INVALID_MESSAGE_PROPERTY = 30409;
export const CHAT_IN_APP_MESSAGE_NOT_AVAILABLE = 30410;
export const CHAT_INVALID_LISTING = 30411;
export const CHAT_INVALID_COMPANION_USER = 30412;

export const CHAT_BANNED_EMAIL = 30413;
export const CHAT_ACCOUNT_IN_REVIEW = 30414;
export const CHAT_SEND_MESSAGE_DUPLICATE_TOO_FREQUEST = 30415;
export const CHAT_SEND_MESSAGE_CREATE_FAILED_LISTING_LOOKUP = 30416;

export const IMPORT_FAILED = 30500;
export const IMPORT_SUPPLIER_INIT_FAILED = 30501;
export const IMPORT_INVALID_SUPPLIER_RS = 30502;
export const IMPORT_USER_CONFLICT = 30503;
export const IMPORT_BITLY_FAILED = 30504;
export const IMPORT_TWITTER_TEXT_NOT_MATCH = 30505;
export const IMPORT_PHOTO_DUPLICATE = 30506;

// GEO
export const INVALID_LONGITUDE = 30600;
export const INVALID_LATITUDE = 30601;

// Data source
export const INVALID_DATA_SOURCE_APIKEY = 30700;

// Twitter oauth
export const TWITTER_INVALID_OAUTH_TOKEN = 30800;
export const TWITTER_INVALID_OAUTH_TOKEN_SECRET = 30801;

// End of Application section
export const INVALID_MESSAGE_ID = 30901;
export const ANTENGO_SHARE_MESSAGE = 30902;

export const ADSENSE_ID = "ca-pub-3154552656014103";
export const TAG_MANAGER_ID = "AW-1006281630";

// TRANSACTION
export const TRANSACTION_INVALID_LISTING = 40000;
export const TRANSACTION_ALREADY_COMPLETED = 40001;
export const TRANSACTION_DUPLICATE_BUYER = 40002;
export const TRANSACTION_SELF_BUYER = 40003;
export const TRANSACTION_MAXIMUM_BUYERS = 40004;
export const TRANSACTION_CREATION_FAILED = 40005;
export const TRANSACTION_ACCESS_DENIED = 40006;
export const TRANSACTION_INVALID_STATUS = 40007;
export const TRANSACTION_STATUS_DENIED = 40008;
export const TRANSACTION_INVALID_TRANSACTION = 40009;
export const TRANSACTION_AUTH_FAILED = 40010;
export const TRANSACTION_PAYMENT_FAILED = 40011;
export const TRANSACTION_ACCOUNT_STATUS_FAILED = 40012;
export const TRANSACTION_SELLER_ACCOUNT_NOT_SETUP = 40013;

export const PAYMENT_ID_REQUIRED = 50001;
export const PAYMENT_CREATION_FAILED = 50002;
export const PAYMENT_COMMISSION_FAILED = 50003;
export const PAYMENT_CAPTURE_FAILED = 50004;
export const PAYMENT_FEE_LOOKUP_FAILED = 50005;
export const PAYMENT_AUTH_EXCEEDED = 50006;
export const PAYMENT_ESCROW_API_FAILED = 50007;
export const PAYMENT_ESCROW_CALL_FAILED = 50008;

export const EMAIL_FAILED_SENDING = 60001;

export const ADDRESS_INVALID_ZIP = 70001;
export const ADDRESS_INVALID_COUNTRY = 70002;
export const ADDRESS_CONNECTED_TRANSACTIONS = 70003;
export const ADDRESS_NOT_FOUND = 70004;
export const ADDRESS_CREATE_FAILED = 70005;
export const ADDRESS_LAST_ADDRESS = 70006;
export const ADDRESS_DUPLICATE = 70007;

export const SHIPPING_DECLINE_FAILED = 80001;
export const SHIPPING_INVALID_LISTING = 80002;
export const SHIPPING_ALREADY_EXISTS = 80003;
export const SHIPPING_INVALID_TO_ADDRESS = 80004;
export const SHIPPING_INVALID_FROM_ADDRESS = 80005;
export const SHIPPING_CREATION_FAILED = 80006;
export const SHIPPING_INVALID_USER = 80007;
export const SHIPPING_SHIPPO_CREATE_FAILED = 80008;
export const SHIPPING_INVALID_DECLINE_TRANSACTION = 80009;
export const SHIPPING_USHIP_FAILED = 80010;
export const SHIPPING_USHIP_CREATE_FAILED = 80011;
export const SHIPPING_USHIP_CHARGE_FAILED = 80012;

export const WIDGET_CACHE_CREATE_FAILED = 90001;
